import { useEffect, useMemo, useState } from "react"

import { Flex, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import Section from "../Section"
import Toggle from "../Toggle"
import TrendsLineChart from "../TrendsLineChart/TrendsLineChart"

import styles from "./categoryTrendsChart.module.less"

const TOOLTIP_ID = "chartjs-tooltip_category"

// "Category trends over time": a full-width line per reason CATEGORY, on the shared trend
// chart (TrendsLineChart).
//
// Categories, not reason codes: a line per code drew a dozen crossing lines nobody could
// read, and it split the same population the category donut already splits. The per-code
// numbers and their own movement live on the breakdown page behind "View all", where a
// chart that follows the category you pick and a two-level table can carry them properly.
const CategoryTrendsChart = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const trends = useSelector(selectSectionData(ANALYTICS_SECTION.CATEGORY_TRENDS))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.CATEGORY_TRENDS))

  useEffect(() => {
    getData(ANALYTICS_SECTION.CATEGORY_TRENDS)
  }, [getData])

  // Count or dollar volume, the same choice the donut offers so the two cards can be read
  // against each other. It also means this card carries the volume view on its own if the
  // donut is ever dropped (see the layout variants). Both come in one answer.
  const [metric, setMetric] = useState("count")
  const translated = useMemo(
    () =>
      (trends?.series ?? []).map((s) => ({
        key: s.key,
        label: t(s.label),
        data: metric === "value" ? s.amount : s.count
      })),
    [trends, metric, t]
  )

  // Header: the count / volume toggle, then "View all" into the breakdown page (same link
  // treatment as the Top countries card).
  const extra = (
    <Flex align="center" gap={12} wrap="wrap" className={styles.head}>
      <Toggle
        value={metric}
        onChange={setMetric}
        options={[
          { value: "count", label: "By count" },
          { value: "value", label: "By volume" }
        ]}
      />
      <Button
        type="link"
        onClick={() => navigate(RoutesPaths.recover.analytics_reason_codes)}
        icon={<Icon type="chevron_right_small" />}
        iconPosition="end"
        style={{ padding: 0 }}
        size="small"
        text={t("View All")}
      />
    </Flex>
  )

  return (
    <Section
      title={t("Category trends over time")}
      tooltip={t(
        "How each dispute category moves over time, by number of disputes or by dollar volume. Open the breakdown to see the individual reason codes behind a category."
      )}
      extra={extra}
    >
      <Spin spinning={fetching}>
        <TrendsLineChart
          labels={trends?.labels}
          unit={trends?.unit}
          series={translated}
          metric={metric}
          tooltipId={TOOLTIP_ID}
        />
      </Spin>
    </Section>
  )
}

export default CategoryTrendsChart
